import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import { Inline } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { Modal } from "PFComponents/modal";
import { getProfileName } from "PFCore/helpers/profile";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { fillAndBookShortlist } from "PFCore/services/shortlist";
import { BasicProfile } from "PFTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { getExceptionDetails } from "../helpers";
import css from "../modals.module.scss";
import { RequestApprovalModalStepOne, RequestApprovalModalStepTwo } from "./request_approval_modal_steps";
import { RequestApprovalModalProps } from "./types";

export const RequestApprovalModal = ({
  onClose,
  exception,
  role,
  engagement,
  shortlistItem,
  onSuccess
}: RequestApprovalModalProps) => {
  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation("auditPlanner");
  const { t: tTranslation } = useTranslation();
  const { getFirstError } = useResponseErrors();

  const [stepIndex, setStepIndex] = useState(0);
  const [finalApprover, setFinalApprover] = useState<BasicProfile | null>(null);
  const [note, setNote] = useState("");

  const handleSubmit = () =>
    fillAndBookShortlist(shortlistItem.id, { approverId: finalApprover!.id, requestReason: note })
      .then(() => {
        if (onSuccess) {
          onSuccess();
        }
        showGrowl({
          id: getGrowlId("approval-request-submitted"),
          message: t("modals.requestSubmittedTo", {
            name: getProfileName(finalApprover)
          }),
          kind: "success",
          hidePreviousByTypeAndId: true,
          ttl: 5000
        });
      })
      .catch((response) => {
        const error = getFirstError(response);
        showGrowl({
          message: error || tTranslation("somethingWentWrong"),
          kind: "error"
        });
      });
  const { isMultiple, keys: exceptionKeys } = getExceptionDetails(exception);

  const title =
    stepIndex === 0 ? (
      t("modals.fillRole")
    ) : (
      <Inline alignItems="center">
        <Icon name="error" color="paletteRed0" />
        {t(`modals.auditPlannerExceptions.${isMultiple ? "multiple" : exceptionKeys[0]}`)}
      </Inline>
    );

  return (
    <Modal
      title={title}
      subtitle={role.name}
      labelOK={stepIndex === 0 ? tTranslation("continueText") : t("modals.requestApproval")}
      onOK={stepIndex === 0 ? () => setStepIndex(1) : handleSubmit}
      closeOnOK={stepIndex > 0}
      disableOKButton={stepIndex === 1 && (!finalApprover || !note)}
      onClose={onClose}
      size="xl"
      classes={{ body: css.modalBody }}
    >
      {stepIndex === 0 && (
        <RequestApprovalModalStepOne
          exception={exception}
          role={role}
          engagement={engagement}
          shortlistItem={shortlistItem}
        />
      )}
      {stepIndex === 1 && (
        <RequestApprovalModalStepTwo
          exception={exception}
          role={role}
          shortlistItem={shortlistItem}
          approver={finalApprover}
          onApproverChange={setFinalApprover}
          note={note}
          onNoteChange={setNote}
        />
      )}
    </Modal>
  );
};
